import { AlchemyStar } from '@creditornot/cb-icons';
import { Modal } from '@creditornot/cbt-modal';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import { Asset, PreparedAsset, PromotedAsset } from 'apis/asset-repository-service/types';
import { useAssetRepositoryPrepareAsset } from 'modules/shared-menu/hooks/useAssetRepositoryPrepareAsset/useAssetRepositoryPrepareAsset';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import {
  ImageGalleryEnhanceImages,
  ImageToEnhance,
} from 'modules/shared-menu/pages/ListingManagerImageGalleryPage/components/ImageGalleryEnhanceImages';

import ActionButton from './ActionButton';
import { getAssetIdFromUrl, useBulkImageEnhancement } from './useBulkImageEnhancement';
import { Offer } from '../../../types';

const ASSET_REPOSITORY_SUB_URL = '/assets/';

type Props = {
  selectedIds: Set<string>;
};

const getAllOfferImages = (offers: Offer[]) => [
  ...offers.flatMap(offer => {
    if (offer.images && offer.images.length > 0) {
      return offer.images.map(image => image.url);
    }
    return [];
  }),
];

export const BulkImageEnhancement = ({ selectedIds }: Props) => {
  const { offers } = useSelectionData();
  const { bulkUpdateOfferImage } = useBulkImageEnhancement();
  const { prepareAssets } = useAssetRepositoryPrepareAsset();
  const selectedOffers = useMemo(() => offers.filter(offer => selectedIds.has(offer.id)), [offers, selectedIds]);
  const allOfferUniqueImages = useMemo(() => Array.from(new Set(getAllOfferImages(selectedOffers))), [selectedOffers]);
  const [isEnhanceImagesModalOpen, setEnhanceImagesModalOpen] = useState<boolean>(false);
  const [isEnhanceImagesModalLoading, setEnhanceImagesModalLoading] = useState<boolean>(false);
  const [skippedImages, setSkippedImages] = useState<Asset['id'][]>([]);

  const imagesToEnhance: ImageToEnhance[] = useMemo(
    () =>
      allOfferUniqueImages
        .reduce((images, imageUrl) => {
          const assetId = getAssetIdFromUrl(imageUrl);
          if (assetId) {
            return [...images, { id: assetId }];
          }
          return images;
        }, [])
        .filter(image => !skippedImages.includes(image.id)),
    [allOfferUniqueImages, skippedImages],
  );

  const imagesNeedToBePrepared = useMemo(
    () => allOfferUniqueImages.filter(imageUrl => !imageUrl.includes(ASSET_REPOSITORY_SUB_URL)),
    [allOfferUniqueImages],
  );

  const handleCloseModal = useCallback(() => {
    setEnhanceImagesModalOpen(false);
    setSkippedImages([]);
  }, []);

  const handleSkipImage = useCallback(
    (imageId: Asset['id']) => {
      if (skippedImages.length === allOfferUniqueImages.length - 1) {
        setEnhanceImagesModalOpen(false);
        setSkippedImages([]);
      } else {
        setSkippedImages(prev => [...prev, imageId]);
      }
    },
    [allOfferUniqueImages, skippedImages],
  );

  const handleEnhancedImagesSave = useCallback(
    async (enhancedImages: PromotedAsset[]) => {
      setEnhanceImagesModalLoading(true);

      const updatedImagesMap = new Map(
        enhancedImages.map(image => [
          process.env.IMAGE_RESIZER_PROXY_URI + ASSET_REPOSITORY_SUB_URL + image.originalAssetId,
          image.imageResizerProxyUrl,
        ]),
      );
      await bulkUpdateOfferImage(selectedOffers, updatedImagesMap);

      setEnhanceImagesModalLoading(false);
      setEnhanceImagesModalOpen(false);
      setSkippedImages([]);
    },
    [selectedOffers, bulkUpdateOfferImage],
  );

  const startEnhancingImages = useCallback(async () => {
    setEnhanceImagesModalOpen(true);

    if (imagesNeedToBePrepared.length < 1) {
      return;
    }

    setEnhanceImagesModalLoading(true);

    try {
      // migrate non-ARS images to ARS
      const preparedAssets = await prepareAssets(imagesNeedToBePrepared);

      const newPreparedImagesMap = new Map();
      preparedAssets.forEach((preparedAsset: PreparedAsset, index: number) =>
        newPreparedImagesMap.set(imagesNeedToBePrepared[index], preparedAsset.imageResizerProxyUrl),
      );

      await bulkUpdateOfferImage(selectedOffers, newPreparedImagesMap);
    } catch (error) {
      setEnhanceImagesModalOpen(false);
      toast.error(<FormattedMessage id="menu.bulkActions.enhanceImagesError" />);
    } finally {
      setEnhanceImagesModalLoading(false);
    }
  }, [
    imagesNeedToBePrepared,
    prepareAssets,
    bulkUpdateOfferImage,
    selectedOffers,
    setEnhanceImagesModalOpen,
    setEnhanceImagesModalLoading,
  ]);

  const isActionButtonDisabled = imagesToEnhance.length < 1 && imagesNeedToBePrepared.length < 1;

  return (
    <>
      <ActionButton
        icon={<AlchemyStar />}
        onClick={startEnhancingImages}
        tooltipTitle={
          <FormattedMessage
            id={isActionButtonDisabled ? 'menu.bulkActions.selectOffersWithImages' : 'menu.bulkActions.enhance-images'}
          />
        }
        disabled={isActionButtonDisabled}
      />
      <Modal open={isEnhanceImagesModalOpen} sizeConfig={{ type: 'fullScreen' }} disableContentPadding>
        <ImageGalleryEnhanceImages
          imagesToEnhance={imagesToEnhance}
          onDeleteImage={handleSkipImage}
          onClose={handleCloseModal}
          onSaveResult={handleEnhancedImagesSave}
          loading={isEnhanceImagesModalLoading}
          loadingCaption={<FormattedMessage id="menu.bulkActions.preparingImagesToEnhance" />}
        />
      </Modal>
    </>
  );
};
